import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Converts a string into a URL-friendly "slug".
 *
 * Steps:
 * 1. Normalize the string using Unicode Normalization Form 'NFD' to separate
 *    base characters from accent marks (diacritics).
 *    For example, 'ò' becomes 'o' + '̀' (combining accent).
 *
 * 2. Remove all combining accent characters using a regex that targets
 *    Unicode diacritical marks in the range U+0300 to U+036F.
 *    This strips accents, leaving only the base characters.
 *
 * 3. Convert the entire string to lowercase to ensure case-insensitivity.
 *
 * 4. Replace all sequences of non-alphanumeric characters (anything other than
 *    a-z and 0-9) with a single hyphen ('-'). This includes spaces, punctuation,
 *    and special symbols.
 *
 * 5. Remove any leading or trailing hyphens to clean up the slug.
 *
 * Example:
 *  'Haitian Donuts (Kokiyòl)' =>
 *  'haitian-donuts-kokiyol'
 *
 * @param {string} name - The input string to convert into a slug.
 * @returns {string} - The generated slug.
 */
export function slugify(name: string): string {
  // TODO: Create a 'slug' field for Products instead
  return name
    .normalize('NFD') // Normalize accents
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphen
    .replace(/(^-|-$)/g, ''); // Remove leading/trailing hyphens
}
